import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/auth-context";
import { api } from "@/lib/api";
import {
  validateVideoFile,
  formatFileSize,
  formatDuration,
  MAX_VIDEO_DURATION_SECONDS,
} from "@/lib/video-validation";
import { Button } from "@/components/ui/button";
import { Upload, AlertCircle, CheckCircle, Loader2, Clock, Trash2 } from "lucide-react";
import { useMyCoachNotes } from "@/hooks/use-coach";
import { CoachNotesDisplay } from "@/components/coach-notes-display";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { ExerciseVideoFeedback, VideoFeedbackAnalysis } from "@shared/schema";
import { trackEvent } from "@/lib/analytics/posthog";

interface VideoFeedbackSectionProps {
  exerciseName: string;
  trainingPlanId?: number;
  dayNumber?: number;
}

type UploadStatus = "idle" | "validating" | "uploading" | "analyzing" | "error";

export function VideoFeedbackSection({
  exerciseName,
  trainingPlanId,
  dayNumber,
}: VideoFeedbackSectionProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [status, setStatus] = useState<UploadStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [existingFeedback, setExistingFeedback] = useState<ExerciseVideoFeedback | null>(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch coach notes for the player
  const { data: coachNotes = [] } = useMyCoachNotes();

  // Check if we have the required context
  const canUpload = !!user && !!trainingPlanId && !!dayNumber;

  // Load existing feedback when component mounts
  useEffect(() => {
    if (canUpload && user?.id && trainingPlanId && dayNumber && exerciseName) {
      loadExistingFeedback();
    } else {
      setIsLoadingFeedback(false);
    }
  }, [canUpload, user?.id, trainingPlanId, dayNumber, exerciseName]);

  const loadExistingFeedback = async () => {
    if (!user?.id || !trainingPlanId || !dayNumber) return;

    setIsLoadingFeedback(true);
    try {
      const result = await api.getExerciseFeedbackHistory(
        user.id.toString(),
        trainingPlanId,
        dayNumber,
        exerciseName
      );
      if (result.success && result.history && result.history.length > 0) {
        // Only one video per exercise - get the first (and only) one
        setExistingFeedback(result.history[0]);
      } else {
        setExistingFeedback(null);
      }
    } catch {
      setExistingFeedback(null);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const handleFileSelect = useCallback(async (file: File) => {
    setError(null);
    setStatus("validating");
    setSelectedFile(file);

    const validationResult = await validateVideoFile(file);
    if (!validationResult.valid) {
      setError(validationResult.error || "Invalid video file");
      setStatus("error");
      setSelectedFile(null);
      trackEvent('video_validation_failed', {
        exercise_name: exerciseName,
        error_reason: validationResult.error,
        file_size: file.size,
        file_type: file.type,
      });
      return;
    }

    setVideoDuration(validationResult.duration || null);
    setStatus("idle");
  }, [exerciseName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("video/")) {
        handleFileSelect(file);
      } else {
        setError("Please drop a video file (MP4, MOV, or WebM)");
      }
    },
    [handleFileSelect]
  );

  const handleUpload = async () => {
    if (!selectedFile || !user?.id || !trainingPlanId || !dayNumber) return;

    setStatus("uploading");
    setError(null);

    try {
      setStatus("analyzing");

      const result = await api.uploadExerciseFeedback(
        user.id.toString(),
        trainingPlanId,
        dayNumber,
        exerciseName,
        selectedFile
      );

      if (result.success && result.feedback) {
        setExistingFeedback(result.feedback);
        setStatus("idle");
        setSelectedFile(null);
        setVideoDuration(null);
        trackEvent('video_feedback_uploaded', {
          exercise_name: exerciseName,
          plan_id: trainingPlanId,
          file_size_mb: selectedFile ? Math.round(selectedFile.size / 1024 / 1024 * 100) / 100 : undefined,
        });
      } else if (result.errorType === "ALREADY_EXISTS" && result.existingFeedback) {
        // Race condition - video was uploaded elsewhere
        setExistingFeedback(result.existingFeedback);
        setStatus("idle");
        setSelectedFile(null);
        setVideoDuration(null);
      } else {
        setError(result.error || "Failed to analyze video");
        setStatus("error");
        trackEvent('video_feedback_failed', {
          exercise_name: exerciseName,
          error_message: result.error,
        });
      }
    } catch {
      setError("Failed to upload video. Please try again.");
      setStatus("error");
      try {
        trackEvent('video_feedback_failed', {
          exercise_name: exerciseName,
          error_message: 'network_error',
        });
      } catch {}
    }
  };

  const handleDelete = async () => {
    if (!existingFeedback || !user?.id) return;

    setIsDeleting(true);
    try {
      const result = await api.deleteExerciseFeedback(existingFeedback.id);
      if (result.success) {
        setExistingFeedback(null);
        trackEvent('video_feedback_deleted', {
          exercise_name: exerciseName,
          feedback_id: existingFeedback.id,
        });
      } else {
        setError(result.error || "Failed to delete video");
        setStatus("error");
      }
    } catch {
      setError("Failed to delete video. Please try again.");
      setStatus("error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setStatus("idle");
    setError(null);
    setVideoDuration(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // If user hasn't saved the plan yet, show a message
  if (!canUpload) {
    return (
      <div>
        <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-3">
          Get Feedback on Your Move
        </h4>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-yellow-200/80 text-sm">
          <p className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Save your training plan to unlock AI form feedback on your exercises.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-3">
        Get Feedback on Your Move
      </h4>

      {/* Loading State */}
      {isLoadingFeedback && (
        <div className="flex items-center justify-center gap-2 text-white/40 text-sm py-8">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading...
        </div>
      )}

      {/* Existing Feedback - Prominent Display */}
      {!isLoadingFeedback && existingFeedback && (
        <>
          <ExistingFeedbackDisplay
            feedback={existingFeedback}
            onDelete={handleDelete}
            isDeleting={isDeleting}
          />
          {/* Coach Notes for this Video */}
          <CoachNotesDisplay
            notes={coachNotes}
            targetType="video"
            targetId={existingFeedback.id}
            title="Coach's Feedback"
            className="mt-4"
          />
        </>
      )}

      {/* Upload Area - Only when no existing feedback */}
      {!isLoadingFeedback && !existingFeedback && (
        <>
          <p className="text-sm text-white/60 mb-4">
            Upload a video of yourself performing this exercise (max {MAX_VIDEO_DURATION_SECONDS} seconds).
          </p>

          <div
            className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              status === "error"
                ? "border-red-500/50 bg-red-500/10"
                : selectedFile
                ? "border-[var(--apple-green)]/50 bg-[var(--apple-green)]/5"
                : "border-white/20 hover:border-white/40 bg-white/5"
            }`}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleInputChange}
              className="hidden"
            />

            {status === "idle" && !selectedFile && (
              <div className="space-y-3">
                <Upload className="w-10 h-10 mx-auto text-white/40" />
                <div>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-white/10 hover:bg-white/20 border-white/20"
                  >
                    Choose Video
                  </Button>
                  <p className="text-xs text-white/40 mt-2">or drag and drop</p>
                </div>
                <p className="text-xs text-white/40">MP4, MOV, or WebM - Max {MAX_VIDEO_DURATION_SECONDS}s</p>
              </div>
            )}

            {status === "validating" && (
              <div className="flex items-center justify-center gap-2 text-white/60">
                <Loader2 className="w-5 h-5 animate-spin" />
                Validating video...
              </div>
            )}

            {selectedFile && status === "idle" && (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-[var(--apple-green)]">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{selectedFile.name}</span>
                </div>
                <div className="text-sm text-white/60">
                  {formatFileSize(selectedFile.size)}
                  {videoDuration && ` - ${formatDuration(videoDuration)}`}
                </div>
                <div className="flex gap-2 justify-center">
                  <Button
                    onClick={handleUpload}
                    className="bg-[var(--apple-green)] text-white hover:bg-[var(--apple-green)]/80"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Get Feedback
                  </Button>
                  <Button variant="outline" onClick={handleClear} className="border-white/20">
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {(status === "uploading" || status === "analyzing") && (
              <div className="space-y-3">
                <Loader2 className="w-10 h-10 mx-auto text-[var(--apple-green)] animate-spin" />
                <p className="text-white/80">
                  {status === "uploading" ? "Uploading video..." : "Analyzing your form..."}
                </p>
                <p className="text-xs text-white/40">This may take up to 30 seconds</p>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-3">
                <AlertCircle className="w-10 h-10 mx-auto text-red-400" />
                <p className="text-red-400">{error}</p>
                <Button variant="outline" onClick={handleClear} className="border-white/20">
                  Try Again
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Component to display existing feedback prominently
function ExistingFeedbackDisplay({
  feedback,
  onDelete,
  isDeleting,
}: {
  feedback: ExerciseVideoFeedback;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const createdAt = new Date(feedback.createdAt);
  const formattedDate = createdAt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="space-y-4">
      {/* Header with date and delete button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-white/60">
          <Clock className="w-4 h-4" />
          <span>Uploaded {formattedDate}</span>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={isDeleting}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Video?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete your video and AI feedback. You can upload a new video afterwards.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Video Player */}
      {feedback.videoUrl && (
        <div className="rounded-xl overflow-hidden bg-black">
          <video
            src={feedback.videoUrl}
            controls
            className="w-full max-h-64"
            playsInline
          />
        </div>
      )}

      {/* Feedback by Status */}
      {feedback.status === "completed" && feedback.feedbackJson && (
        <div className="bg-[var(--apple-green)]/10 border border-[var(--apple-green)]/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-[var(--apple-green)] mb-3">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">Analysis</span>
          </div>
          <FeedbackDisplay feedback={feedback.feedbackJson} />
        </div>
      )}

      {feedback.status === "processing" && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-yellow-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Analysis in progress...</span>
          </div>
          <p className="text-xs text-white/40 mt-2">This may take up to 30 seconds</p>
        </div>
      )}

      {feedback.status === "failed" && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{feedback.errorMessage || "Analysis failed"}</span>
          </div>
          <p className="text-xs text-white/40 mt-2">Delete this video to try again</p>
        </div>
      )}
    </div>
  );
}

// Component to display feedback analysis
function FeedbackDisplay({ feedback }: { feedback: VideoFeedbackAnalysis | null }) {
  if (!feedback?.analysis) return null;

  const categories = [
    { key: "alignment_posture", label: "Alignment & Posture", icon: "🎯" },
    { key: "range_of_motion", label: "Range of Motion", icon: "📏" },
    { key: "tempo_control", label: "Tempo Control", icon: "⏱️" },
    { key: "biggest_error", label: "Biggest Error", icon: "⚠️" },
    { key: "injury_risk", label: "Injury Risk", icon: "🩹" },
    { key: "whats_working", label: "What's Working", icon: "✅" },
  ];

  return (
    <div className="space-y-3">
      {categories.map(({ key, label, icon }) => {
        const category = feedback.analysis[key as keyof typeof feedback.analysis];
        if (!category) return null;

        const isPositive = key === "whats_working";
        const fixOrMaintain = isPositive
          ? (category as any).maintain
          : (category as any).fix;

        // Skip if no observation or it's a "no issues" type response
        if (!category.observation || category.observation.toLowerCase().includes("no issues")) {
          return null;
        }

        return (
          <div
            key={key}
            className={`p-3 rounded-lg ${
              isPositive ? "bg-green-500/10 border border-green-500/20" : "bg-white/5"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span>{icon}</span>
              <span className="font-medium text-sm text-white/90">{label}</span>
              {category.timestamp && category.timestamp !== "N/A" && (
                <span className="text-xs text-white/40 ml-auto">{category.timestamp}</span>
              )}
            </div>
            <p className="text-sm text-white/70 mb-1">{category.observation}</p>
            {fixOrMaintain && fixOrMaintain !== "N/A" && (
              <p className={`text-sm ${isPositive ? "text-green-400" : "text-[var(--apple-green)]"}`}>
                {isPositive ? "Keep it up: " : "Fix: "}
                {fixOrMaintain}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
